import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserSiteAssignments,
  assignUserToSites,
  removeUserFromSite,
  SiteAssignmentDto,
} from '../api/userSiteAssignmentsApi';
import { useAuthStore } from '../../auth/stores/authStore';
import { onboardingKeys } from '../../onboarding/hooks/onboardingKeys';
import { userKeys } from './userKeys';

/**
 * Query hook to fetch site assignments for a specific user
 */
export function useUserSiteAssignments(userId: string | null) {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  return useQuery<SiteAssignmentDto[], Error>({
    queryKey: userKeys.siteAssignments(userId || ''),
    queryFn: () => {
      if (!userId) {
        return [];
      }
      return getUserSiteAssignments(userId);
    },
    enabled: isAuthenticated && !!userId,
  });
}

/**
 * Mutation hook to assign a user to multiple sites.
 * Passing an empty array removes all assignments.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export const useAssignSitesToUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, siteIds }: { userId: string; siteIds: number[] }) =>
      assignUserToSites(userId, siteIds),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: userKeys.siteAssignments(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
};

/**
 * Mutation hook to remove a user from a specific site.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export const useRemoveUserFromSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, siteId }: { userId: string; siteId: number }) =>
      removeUserFromSite(userId, siteId),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: userKeys.siteAssignments(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.userId),
      });
    },
  });
};
